import { cosA, iatan2, idiv, imax, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Hero, KneesRules, KneesState, SendaNpc } from '../types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { pulseLand } from './brasa.ts';

/**
 * The knees (docs/sendas/artifice.md, chapter 5, act 1; the lesson of chapter 1, turned round). Tormo's hall comes down
 * round him; nothing can hurt him yet (what moves him is deep inside). Dunna leads Nadd by the hand round the hall's way
 * (`way`, the ring of floor round the rubble) and Tormo walks it after them, `gap` behind, at his pace less `lockSlow`
 * for each joint locked: within `grab` he takes Nadd and it is lost.
 *
 * His knees and elbows are gear trains in sight (`joints`): gears in a row, the first driving it (its red arrow), each
 * turning the other way from the one before. Each joint has two or three free axles, each touching two gears of its
 * train. A gear put on an axle that touches two gears turning opposite ways would have to turn both ways at once: the
 * joint jams (chapter 1's error, wanted now). There is one such axle a joint; a gear put on any other only spins: it
 * sparks, and Tormo lunges `lunge` after Nadd. The way of each gear is worked out by counting from the arrow, as in
 * chapter 1. A tap on an axle's mark sends the hero after it (it chases the joint as he walks) and standing still by it
 * `stand` puts the gear.
 *
 * His stomp (`stomp`, warned 3 s) strikes the ring round his feet from `inR` to `outR` (close in by his knees is safe,
 * his elbows are in it); he rests `rest` after. The roof drops stone (`debris`: one on the hero, one on the people);
 * shades come out of the cracks (`foes`) for whoever is nearest. Won with the four locked (`fall` later: on his knees).
 *
 * Cost: two points along a ring of 36 a tick; the joints' trains are read, never worked out (each axle's answer is its
 * two gears' distance).
 */

/** How close the hero comes to a joint's axle before it stops to work it. */
const CLOSE = 450;

/** Marks: joint × 4 + its free axle. */
export const KNEE_AXLES = 4;

const fresh = (r: KneesRules): KneesState => ({
  at: r.gap,
  gap: r.gap,
  x: 0,
  y: 0,
  face: 0,
  locked: r.joints.map(() => false),
  tried: r.joints.map(() => 0),
  job: -1,
  held: 0,
  stomp: r.stomp.first,
  stompAt: -1,
  still: 0,
  debris: r.debris.first,
  spots: [],
  foe: r.foes.first,
  lunges: 0,
  near: false,
  hand: false,
  quake: r.quake.first,
  quakeAt: -1,
  down: -1,
  took: -1,
});

/** The point `d` along the way (round and round) and the way it runs there. */
export const kneeWayAt = (r: KneesRules, d: number): { x: number; y: number; face: number } => {
  const n = r.way.length;
  let t = d % r.total;
  if (t < 0) t += r.total;
  let i = 0;
  while (i < n - 1 && (r.len[i + 1] as number) <= t) i++;
  const a = r.way[i] as { x: number; y: number };
  const b = r.way[(i + 1) % n] as { x: number; y: number };
  const seg = imax(1, (r.len[i + 1] as number) - (r.len[i] as number));
  const k = t - (r.len[i] as number);
  return { x: a.x + idiv((b.x - a.x) * k, seg), y: a.y + idiv((b.y - a.y) * k, seg), face: iatan2(b.y - a.y, b.x - a.x) };
};

/** A point of Tormo's frame (x to his right, y ahead) in the world. */
const frame = (w: Pick<KneesState, 'x' | 'y' | 'face'>, ox: number, oy: number): { x: number; y: number } => {
  const c = cosA(w.face);
  const sn = sinA(w.face);
  return { x: w.x + idiv(oy * c - ox * sn, 10000), y: w.y + idiv(oy * sn + ox * c, 10000) };
};

/** Where joint `j` is now. */
export const kneeJointAt = (r: KneesRules, w: Pick<KneesState, 'x' | 'y' | 'face'>, j: number): { x: number; y: number } => {
  const J = r.joints[j];
  return J ? frame(w, J.off.x, J.off.y) : { x: w.x, y: w.y };
};

/** Where free axle `k` of joint `j` is now (spread along his frame's forward axis). */
export const kneeAxleAt = (r: KneesRules, w: Pick<KneesState, 'x' | 'y' | 'face'>, j: number, k: number): { x: number; y: number } => {
  const J = r.joints[j];
  if (!J) return { x: w.x, y: w.y };
  return frame(w, J.off.x + (J.off.x < 0 ? -110 : 110), J.off.y + (2 * k - J.free.length + 1) * 190);
};

/** The way gear `g` of joint `j` turns (+1 the drive's way). */
export const kneeGearWay = (r: KneesRules, j: number, g: number): number => {
  const J = r.joints[j];
  return J ? (g % 2 === 0 ? J.drive : -J.drive) : 0;
};

/** The first heap's mark (after the joints' axles). */
export const KNEE_PILES = 4 * KNEE_AXLES;

/** The hero's pace with a gear in its hands. */
export const kneeCarryBp = (s: BattleState): number => (s.senda?.knees?.hand ? (s.rules?.senda?.knees?.carryBp ?? 10000) : 10000);

/** A tap on a heap (the hero goes for a gear) or on a joint's free axle (it goes after it, a gear in its hands). */
export const kneeOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.knees;
  const w = s.senda?.knees;
  if (!r || !w || w.down >= 0 || at < 0) return;
  if (at >= KNEE_PILES) {
    const p = r.piles[at - KNEE_PILES];
    if (!p || w.hand) return;
    w.job = at;
    w.held = 0;
    orderMove(s, h, p.x, p.y);
    h.target = 0;
    h.forced = false;
    return;
  }
  if (!w.hand) return;
  const j = idiv(at, KNEE_AXLES);
  const k = at % KNEE_AXLES;
  const J = r.joints[j];
  if (!J || w.locked[j] || k >= J.free.length) return;
  w.job = at;
  w.held = 0;
  const p = kneeAxleAt(r, w, j, k);
  orderMove(s, h, p.x, p.y);
  h.target = 0;
  h.forced = false;
};

const heroWork = (s: BattleState, r: KneesRules, w: KneesState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  if (w.job >= KNEE_PILES) {
    const p = r.piles[w.job - KNEE_PILES] as { x: number; y: number };
    if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
      w.held = 0;
      if (!h.moving) w.job = -1;
      return;
    }
    if (++w.held < r.take) return;
    w.held = 0;
    w.job = -1;
    w.hand = true;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: p.x, y: p.y });
    return;
  }
  const j = idiv(w.job, KNEE_AXLES);
  const k = w.job % KNEE_AXLES;
  const J = r.joints[j];
  if (!J || w.locked[j] || !w.hand) {
    w.job = -1;
    return;
  }
  const p = kneeAxleAt(r, w, j, k);
  const inReach = withinRange(h.x, h.y, p.x, p.y, r.reach);
  // It chases the joint as Tormo walks (aimed again every half second) till it is right by it; then it stops and
  // works, and keeps working while the joint stays within reach.
  if (!inReach || (h.moving && !withinRange(h.x, h.y, p.x, p.y, CLOSE))) {
    if (!inReach) w.held = 0;
    if (!h.moving || s.tick % 6 === 0) orderMove(s, h, p.x, p.y);
    return;
  }
  if (h.moving) {
    h.moving = false;
    h.path = [];
    h.destX = h.ax = h.x;
    h.destY = h.ay = h.y;
  }
  if (++w.held < r.stand) return;
  w.held = 0;
  w.job = -1;
  // The gear goes in (or spins off where it doesn't jam: lost either way).
  w.hand = false;
  if (k === J.jam) {
    w.locked[j] = true;
    const n = w.locked.filter(Boolean).length;
    ev(s, { t: 'senda', kind: 'kneeLock', npc: '', x: p.x, y: p.y, n: j });
    cue(`lock${n}`);
    if (n === r.joints.length) {
      w.down = s.tick;
      ev(s, { t: 'senda', kind: 'kneesDown', npc: '', x: w.x, y: w.y });
      cue('fall');
    }
    return;
  }
  w.tried[j] = (w.tried[j] as number) | (1 << k);
  w.gap -= r.lunge;
  w.lunges++;
  ev(s, { t: 'senda', kind: 'kneeSpark', npc: '', x: p.x, y: p.y, n: w.job < 0 ? j * KNEE_AXLES + k : w.job });
  cue('wrong');
};

/** His stomp: warned, then the ring round his feet. */
const stomp = (s: BattleState, r: KneesRules, w: KneesState): void => {
  const S = r.stomp;
  if (w.stompAt < 0 && s.waveT >= w.stomp) {
    w.stompAt = s.tick + S.warn;
    ev(s, { t: 'senda', kind: 'stompWarn', npc: '', x: w.x, y: w.y, n: S.warn });
    return;
  }
  if (w.stompAt < 0 || s.tick < w.stompAt) return;
  w.stompAt = -1;
  w.stomp = s.waveT + S.every;
  w.still = s.tick + S.rest;
  ev(s, { t: 'senda', kind: 'stomp', npc: '', x: w.x, y: w.y });
  const inRing = (x: number, y: number): boolean => withinRange(x, y, w.x, w.y, S.outR) && !withinRange(x, y, w.x, w.y, S.inR);
  for (const h of s.heroes) if (h.downTicks === 0 && inRing(h.x, h.y)) damageHero(s, h, mulBp(h.stats.maxHp, S.heroBp), null, 'physical', true);
  for (const n of s.senda?.npcs ?? []) {
    if (n.hp <= 0 || n.away || n.id === 'nadd' || !inRing(n.x, n.y)) continue;
    n.hp = imax(0, n.hp - S.blows);
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/** His steps shake the whole hall: warned, then each joint still free a share of the hero's life, wherever it stands. */
const quake = (s: BattleState, r: KneesRules, w: KneesState): void => {
  const Q = r.quake;
  if (w.quakeAt < 0 && s.waveT >= w.quake) {
    w.quakeAt = s.tick + Q.warn;
    ev(s, { t: 'senda', kind: 'quakeWarn', npc: '', x: w.x, y: w.y, n: Q.warn });
    return;
  }
  if (w.quakeAt < 0 || s.tick < w.quakeAt) return;
  w.quakeAt = -1;
  w.quake = s.waveT + Q.every - Q.warn;
  const free = w.locked.filter((x) => !x).length;
  ev(s, { t: 'senda', kind: 'quake', npc: '', x: w.x, y: w.y, n: free });
  if (free > 0) for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, Q.bp * free), null, 'physical', true);
};

/** Stone off the roof, on the hero (Dunna and Nadd, hand in hand, can't step out of it: none on them). */
const debris = (s: BattleState, r: KneesRules, w: KneesState): void => {
  if (s.waveT < w.debris) return;
  w.debris = s.waveT + r.debris.every;
  const t = s.tick + r.debris.warn;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const add = (x: number, y: number): void => {
    w.spots.push({ x, y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: r.debris.warn });
  };
  if (h) add(h.x + rngRange(s.rng, -300, 300), h.y + rngRange(s.rng, -300, 300));
};

/** The people on the way: Dunna ahead, Nadd by her hand. */
const placePeople = (s: BattleState, r: KneesRules, w: KneesState): void => {
  for (const n of s.senda?.npcs ?? []) {
    if (n.id !== 'dunna' && n.id !== 'nadd') continue;
    const p = kneeWayAt(r, w.at - (n.id === 'nadd' ? 380 : 0));
    n.x = p.x;
    n.y = p.y;
    n.facing = p.face;
  }
};

const placeTormo = (r: KneesRules, w: KneesState): void => {
  const p = kneeWayAt(r, w.at - w.gap);
  w.x = p.x;
  w.y = p.y;
  w.face = p.face;
};

export const kneesTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.knees;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.knees ??= fresh(r));
  if (!s.started || w.down >= 0 || w.took >= 0) {
    placePeople(s, r, w);
    placeTormo(r, w);
    return;
  }
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna');
  if (d && d.hp > 0) w.at += r.people;
  const locks = w.locked.filter(Boolean).length;
  if (s.tick >= w.still) w.gap += (d && d.hp > 0 ? r.people : 0) - imax(0, r.tormo - locks * r.lockSlow);
  else if (d && d.hp > 0) w.gap += r.people;
  placePeople(s, r, w);
  placeTormo(r, w);
  heroWork(s, r, w, cue);
  stomp(s, r, w);
  quake(s, r, w);
  debris(s, r, w);
  pulseLand(s, w.spots, r.debris);
  if (s.waveT >= w.foe) {
    w.foe = s.waveT + r.foes.every;
    const at = r.holes[rngInt(s.rng, r.holes.length)] as {
      x: number;
      y: number;
    };
    for (let k = 0; k < r.foes.n; k++) {
      const e = spawnEnemy(s, rngInt(s.rng, 2) === 0 ? 'wraith' : 'skeleton', 1, false, 0, at.x + rngRange(s.rng, -300, 300), at.y + rngRange(s.rng, -300, 300));
      if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
    }
    ev(s, { t: 'senda', kind: 'thaw', npc: '', x: at.x, y: at.y, n: r.foes.n });
  }
  if (!w.near && w.gap <= r.grab + 2600) {
    w.near = true;
    cue('near');
  }
  if (w.gap <= r.grab) {
    w.took = s.tick;
    ev(s, { t: 'senda', kind: 'tormoTakes', npc: 'nadd', x: w.x, y: w.y });
  }
};

/** The four joints. */
export const kneesGoal = (s: BattleState): { done: number; need: number } => {
  const w = s.senda?.knees;
  const n = s.rules?.senda?.knees?.joints.length ?? 4;
  return { done: w ? w.locked.filter(Boolean).length : 0, need: n };
};

export const kneesResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.knees;
  const w = s.senda?.knees;
  if (!r || !w) return null;
  if (w.took >= 0) return 'lost';
  return w.down >= 0 && s.tick >= w.down + r.fall ? 'won' : null;
};
